import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { useDebounce } from "use-debounce";

import "@/Utils/pdfWorker";
import { PDF_LOAD_OPTIONS } from "@/Utils/pdfLoadOptions";
import { usePdfPageWindow } from "@/Utils/usePdfPageWindow";
import { useChat } from "@/Components/AChatWindow/hooks/useChat";
import ChatWindow from "@/Components/AChatWindow/components/ChatWindow";

import { useLibraryDocumentViewer } from "../../context/LibraryDocumentViewerContext";

import PageRail, { PageRailPages } from "./PageRail";
import ViewerToolbar from "./ViewerToolbar";
import DetailsPanel from "./DetailsPanel";
import { usePdfSearch } from "./usePdfSearch";
import "./LibraryDocumentViewer.css";
import TagSearch from "./tags-search";

const getFileUrl = (doc) => doc?.upload_url || doc?.url || doc?.file || "";

const pdfPageStyle = {
  position: "relative",
  marginBottom: "5px",
  marginTop: "5px",
  marginLeft: "auto",
  marginRight: "auto",
  boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.09)",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
};

const LibraryDocumentViewer = () => {
  const { isOpen, document: libraryDoc, closeDocument, notifyUpdated } = useLibraryDocumentViewer();

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  // Real size of every page that has rendered at least once, so placeholders
  // for pages not yet rendered are the right height and nothing shifts.
  const [pageSizes, setPageSizes] = useState({});
  // Whether some page has already set the baseline size and fit zoom.
  const baselineSized = useRef(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [defaultZoom, setDefaultZoom] = useState("fit");
  const [containerRef, setContainerRef] = useState(null);
  // The rail's scroll container (observer root) and the element its thumbnails
  // are portaled into. Callback refs as state, matching containerRef above.
  const [railScrollEl, setRailScrollEl] = useState(null);
  const [railPortalEl, setRailPortalEl] = useState(null);
  const [updatedUser, setUpdatedUser] = useState(null);
  const [chatDocumentThread, setChatDocumentThread] = useState(false);
  const {
    firms,
    selectedFirm,
    users,
    loading: chatLoading,
    error: chatError,
    handleSelectFirm,
    sendMessage,
    searchUsers,
    messages,
    typingUsers,
    handleTyping,
    markAsRead,
    loadMoreMessages,
    handleSelectUser,
  } = useChat({ skipInitialization: true });
  const observer = useRef(null);

  const fileUrl = getFileUrl(libraryDoc);
  const { matchingPages, progress: searchProgress } = usePdfSearch(pdfDoc, debouncedSearchTerm);
  useEffect(() => {
    if (!isOpen) return;
    setNumPages(0);
    setPageNumber(1);
    setSearchTerm("");
    setError("");
    setZoomLevel(1);
    setPageDimensions({ width: 0, height: 0 });
    setPageSizes({});
    setPdfDoc(null);
    baselineSized.current = false;
    setDefaultZoom("fit");
    setChatDocumentThread(false); // add
    setUpdatedUser(null); // add
  }, [isOpen, fileUrl]);
  // useEffect(() => {
  //   if (!isOpen) return;
  //   setNumPages(0);
  //   setPageNumber(1);
  //   setSearchTerm("");
  //   setError("");
  //   setZoomLevel(1);
  //   setPageDimensions({ width: 0, height: 0 });
  //   setDefaultZoom("fit");
  // }, [isOpen, fileUrl]);

  const scrollPageToView = (page, updatePageNumber = (p) => p) => {
    const el = document.getElementById(`lib-doc-page-${page}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      updatePageNumber(page);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || (numPages && page > numPages)) return;
    scrollPageToView(page, setPageNumber);
  };

  const onZoomChange = (zoomType) => {
    const containerWidth = containerRef?.offsetWidth || 0;
    const containerHeight = containerRef?.offsetHeight || 0;
    switch (zoomType) {
      case "in":
        setZoomLevel((z) => Math.min(z + 0.1, 4));
        setDefaultZoom("");
        break;
      case "out":
        setZoomLevel((z) => Math.max(z - 0.1, 0.5));
        setDefaultZoom("");
        break;
      case "width": {
        const widthScale = containerWidth / pageDimensions.width;
        setZoomLevel(Math.floor(widthScale * 10) / 10 - 0.1);
        setDefaultZoom("");
        break;
      }
      case "height": {
        setZoomLevel(containerHeight / pageDimensions.height);
        setDefaultZoom("");
        break;
      }
      case "fit": {
        const fitScale = Math.min(
          containerWidth / pageDimensions.width,
          containerHeight / pageDimensions.height
        );
        setZoomLevel(Math.max(fitScale - 0.05, 0.1));
        setDefaultZoom("fit");
        break;
      }
      default:
        if (typeof zoomType === "number") {
          setZoomLevel(zoomType);
          setDefaultZoom("");
        }
        break;
    }
  };

  const handleZoomDefault = (width, height) => {
    if (!containerRef) return;
    const containerWidth = containerRef.offsetWidth || 0;
    const containerHeight = containerRef.offsetHeight || 0;
    const fitScale = Math.min(containerWidth / width, containerHeight / height);
    setZoomLevel(Math.max(fitScale - 0.05, 0.1));
  };

  const onPageLoadSuccess = (page) => {
    const { width, height } = page.getViewport({ scale: 1 });

    setPageSizes((prev) =>
      prev[page.pageNumber]?.width === width && prev[page.pageNumber]?.height === height
        ? prev
        : { ...prev, [page.pageNumber]: { width, height } }
    );

    // Only the first page to render sizes the view. Letting every page do it
    // meant the fit-to-view zoom was recomputed once per page and ended up set
    // by whichever page happened to load last.
    if (baselineSized.current) return;
    baselineSized.current = true;

    setPageDimensions({ width, height });
    if (defaultZoom === "fit") handleZoomDefault(width, height);
  };

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef || !pageDimensions.width || !pageDimensions.height) return;
      if (defaultZoom === "fit") {
        handleZoomDefault(pageDimensions.width, pageDimensions.height);
      } else {
        const widthScale = containerRef.offsetWidth / pageDimensions.width;
        setZoomLevel(Math.max(widthScale - 0.05, 0.1));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDimensions, containerRef, defaultZoom]);

  // Keyed on the first match rather than the array so later matches arriving
  // during an incremental search don't re-scroll to the same page.
  const firstMatch = matchingPages[0];
  useEffect(() => {
    if (firstMatch) scrollPageToView(firstMatch, setPageNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstMatch]);

  // Pages close enough to the viewport to be worth rendering. Everything else
  // stays a sized placeholder, which is what stops pdf.js fetching the bytes
  // for the whole document.
  const visiblePages = usePdfPageWindow(containerRef, numPages, {
    selector: ".lib-viewer-page",
  });

  // Sizes placeholders for pages that have not rendered yet. Correct for the
  // uniform-page documents this app handles, and each page corrects itself the
  // first time it renders.
  const baselineSize = pageDimensions.width ? pageDimensions : null;

  useEffect(() => {
    if (!containerRef) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const p = parseInt(entry.target.getAttribute("data-page-number"));
            if (p) setPageNumber(p);
          }
        });
      },
      { root: containerRef, rootMargin: "0px", threshold: 0.5 }
    );

    containerRef.querySelectorAll(".lib-viewer-page").forEach((el) => io.observe(el));
    observer.current = io;

    return () => io.disconnect();
  }, [containerRef, numPages]);

  if (!isOpen || !libraryDoc) return null;

  return (
    <Modal
      show={isOpen}
      onHide={closeDocument}
      size="xl"
      centered
      className="universal-document-modal custom-modal-width"
      dialogClassName="document-modal-padding-setting"
    >
      <Modal.Header
        className="document-modal-header d-flex align-items-center justify-content-center height-25"
        style={{ borderRadius: "0px", borderBottom: "none" }}
      >
        <Modal.Title
          id="contained-modal-title-vcenter"
          style={{ fontSize: "14px", fontWeight: "600", textTransform: "uppercase" }}
          className="d-flex align-items-center justify-content-center height-25"
        >
          Library Document Viewer
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="document-modal-body d-flex">
        <div
          className="d-flex flex-column justify-content-between m-0"
          style={{ width: "100%", minWidth: 0 }}
        >
          <div
            className="d-flex"
            style={{ height: "calc(100% - 25px)", width: "100%", minWidth: 0 }}
          >
            <PageRail scrollRef={setRailScrollEl} portalRef={setRailPortalEl} />

            <div className="lib-viewer-main" style={{ flexGrow: 1, minWidth: 0 }}>
              <TagSearch
                documentId={libraryDoc.id}
                documentTags={libraryDoc.tags}
                onTagsChange={notifyUpdated}
              />
              {error && <div className="lib-viewer-error">{error}</div>}

              <div
                className="lib-viewer-canvas"
                style={{ overflowY: "scroll", overflowX: "auto", scrollbarWidth: "none" }}
                ref={setContainerRef}
              >
                <Document
                  file={fileUrl}
                  options={PDF_LOAD_OPTIONS}
                  onLoadSuccess={(pdf) => {
                    setNumPages(pdf.numPages);
                    // Held so text search can reuse this transport instead of
                    // downloading the file a second time.
                    setPdfDoc(pdf);
                  }}
                  onLoadError={() => setError("Failed to load this document")}
                >
                  {/* Thumbnails render as children of this <Document> so they
                      share its single load, but are portaled into the rail so
                      the layout is unchanged. Portals preserve React context,
                      so <Page> still finds react-pdf's DocumentContext. */}
                  {railPortalEl &&
                    createPortal(
                      <PageRailPages
                        numPages={numPages}
                        currentPage={pageNumber}
                        scrollRootEl={railScrollEl}
                        pageSizes={pageSizes}
                        baselineSize={baselineSize}
                        onThumbnailClick={scrollPageToView}
                        setPageNumber={setPageNumber}
                      />,
                      railPortalEl
                    )}

                  {Array.from({ length: numPages }, (_, index) => {
                    const page = index + 1;
                    const size = pageSizes[page] || baselineSize;

                    return (
                      <div
                        key={index}
                        id={`lib-doc-page-${page}`}
                        data-page-number={page}
                        data-window-key={page}
                        className="lib-viewer-page pdf-page"
                        style={{
                          ...pdfPageStyle,
                          width: size ? size.width * zoomLevel : "min-content",
                          height: size ? size.height * zoomLevel : undefined,
                        }}
                      >
                        {visiblePages.has(page) && (
                          <Page
                            pageNumber={page}
                            scale={zoomLevel}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onLoadSuccess={onPageLoadSuccess}
                          />
                        )}
                      </div>
                    );
                  })}
                </Document>
              </div>
            </div>
            <DetailsPanel
              document={libraryDoc}
              onClose={closeDocument}
              onSaved={notifyUpdated}
              onDeleted={() => {
                notifyUpdated({ _deleted: true });
                closeDocument();
              }}
              fileUrl={fileUrl}
              firms={firms}
              selectedFirm={selectedFirm}
              users={users}
              chatLoading={chatLoading}
              chatError={chatError}
              handleSelectFirm={handleSelectFirm}
              sendMessage={sendMessage}
              searchUsers={searchUsers}
              handleSelectUser={handleSelectUser}
              setUpdatedUser={setUpdatedUser}
              setChatDocumentThread={setChatDocumentThread}
            />
          </div>

          <ViewerToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchProgress={searchProgress}
            pageNumber={pageNumber}
            totalPages={numPages}
            onGoToPage={goToPage}
            zoom={zoomLevel}
            onZoomChange={(mode) => {
              if (mode === "toggleDropdown") return;
              onZoomChange(mode);
            }}
          />
        </div>

        {chatDocumentThread && updatedUser && (
          <div style={{ minWidth: "200px", flex: "0 0 30%" }}>
            <ChatWindow
              user={updatedUser}
              messages={messages}
              typingUsers={typingUsers}
              sendMessage={sendMessage}
              handleSendMessage={(text) => sendMessage(updatedUser?.id, text)}
              handleTyping={handleTyping}
              markAsRead={markAsRead}
              loadMoreMessages={loadMoreMessages}
              source={"chat"}
            />
          </div>
        )}
        {/* <DetailsPanel
          document={libraryDoc}
          onClose={closeDocument}
          onSaved={notifyUpdated}
          onDeleted={() => {
            notifyUpdated({ _deleted: true });
            closeDocument();
          }}
          
        /> */}
      </Modal.Body>
    </Modal>
  );
};

export default LibraryDocumentViewer;
